import React from "react";
import classNames from "classnames";

import { Context } from "../context";
import { useInfiniteScroll } from "@common/infiniteScroll";
import { Props } from "./types";
import styles from "./styles.scss";

function ScrollBar({
  id,
  orientation,
  scrollSize,
  withInfiniteLoad,
  children,
  className,
  scroll,
  updateRef,
}: Props) {
  const infiniteLoadingRef = useInfiniteScroll();
  const [isDragging, setIsDragging] = React.useState(false);
  const basicRef = React.useRef<HTMLDivElement | null>(null);
  const track = withInfiniteLoad ? infiniteLoadingRef : basicRef;
  const thumbRef = React.useRef<HTMLDivElement | null>(null);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const { registerGutter, removeGutter } = React.useContext(Context);
  const dragCleanupRef = React.useRef<(() => void) | null>(null);

  React.useEffect(() => {
    registerGutter({ id, ref: track });

    return () => {
      removeGutter(id);
    };
  }, [id]);

  React.useEffect(() => {
    updateRef && updateRef(track);
  }, [updateRef]);

  React.useEffect(() => {
    return () => {
      if (dragCleanupRef.current) dragCleanupRef.current();
    };
  }, []);

  const getTrackMetrics = (rect: DOMRect) => {
    const trackSize = orientation === "horizontal" ? rect.width : rect.height;
    const maxScroll = Math.max(0, scrollSize - trackSize);
    const thumbSize =
      maxScroll > 0 ? (trackSize / scrollSize) * trackSize : trackSize;
    return { trackSize, maxScroll, thumbSize };
  };

  const getThumbPosition = (
    currentScroll: number,
    maxScroll: number,
    trackSize: number,
    thumbSize: number,
  ) => {
    if (maxScroll === 0) return 0;
    return (currentScroll / maxScroll) * (trackSize - thumbSize);
  };

  const scrollFromThumbPosition = (
    thumbStart: number,
    trackSize: number,
    thumbSize: number,
    maxScroll: number,
  ) => {
    const range = trackSize - thumbSize;
    if (range <= 0) return 0;
    const ratio = Math.max(0, Math.min(1, thumbStart / range));
    return ratio * maxScroll;
  };

  const updateVisibleThumb = () => {
    if (!thumbRef.current || !track.current || !wrapperRef.current) return;
    const trackEl = track.current;
    const trackSize =
      orientation === "horizontal" ? trackEl.clientWidth : trackEl.clientHeight;
    const currentScroll =
      orientation === "horizontal" ? trackEl.scrollLeft : trackEl.scrollTop;
    const maxScroll = Math.max(0, scrollSize - trackSize);

    if (maxScroll === 0) {
      wrapperRef.current.classList.add(styles.noScroll);
      return;
    }

    wrapperRef.current.classList.remove(styles.noScroll);

    const thumbSize = (trackSize / scrollSize) * trackSize;
    const thumbPos = (currentScroll / maxScroll) * (trackSize - thumbSize);

    if (orientation === "horizontal") {
      thumbRef.current.style.transform = `translateX(${thumbPos}px)`;
      thumbRef.current.style.width = `${thumbSize}px`;
    } else {
      thumbRef.current.style.transform = `translateY(${thumbPos}px)`;
      thumbRef.current.style.height = `${thumbSize}px`;
    }
  };

  React.useEffect(() => {
    updateVisibleThumb();
    const rafId = requestAnimationFrame(() => updateVisibleThumb());
    return () => cancelAnimationFrame(rafId);
  }, [scrollSize, orientation]);

  return (
    <div
      ref={wrapperRef}
      className={classNames(styles.scrollBar, {
        [styles.horizontal]: orientation === "horizontal",
        [styles.vertical]: orientation === "vertical",
        [styles.dragging]: isDragging,
      })}
    >
      <div
        className={classNames(styles.content, {
          [`${className}`]: !!className,
        })}
      >
        {children}
      </div>
      <div
        ref={track}
        className={styles.track}
        onMouseDown={(e) => {
          if (!track.current) return;
          e.preventDefault();

          const rect = e.currentTarget.getBoundingClientRect();
          const clickPos =
            orientation === "horizontal"
              ? e.clientX - rect.left
              : e.clientY - rect.top;
          const { trackSize, maxScroll, thumbSize } = getTrackMetrics(rect);

          if (maxScroll === 0) return;

          const currentScroll =
            orientation === "horizontal"
              ? track.current.scrollLeft
              : track.current.scrollTop;
          const thumbStart = getThumbPosition(
            currentScroll,
            maxScroll,
            trackSize,
            thumbSize,
          );
          const thumbEnd = thumbStart + thumbSize;

          let grabOffset: number;

          if (clickPos >= thumbStart && clickPos <= thumbEnd) {
            grabOffset = clickPos - thumbStart;
          } else {
            grabOffset = thumbSize / 2;
            const newThumbStart = clickPos - grabOffset;
            const newScroll = scrollFromThumbPosition(
              newThumbStart,
              trackSize,
              thumbSize,
              maxScroll,
            );
            orientation === "horizontal"
              ? track.current.scrollTo(newScroll, 0)
              : track.current.scrollTo(0, newScroll);
          }

          setIsDragging(true);

          const handleMouseMove = (ev: MouseEvent) => {
            if (!track.current) return;
            const trackRect = track.current.getBoundingClientRect();
            const mousePos =
              orientation === "horizontal"
                ? ev.clientX - trackRect.left
                : ev.clientY - trackRect.top;
            const metrics = getTrackMetrics(trackRect);
            const newThumbStart = mousePos - grabOffset;
            const newScroll = scrollFromThumbPosition(
              newThumbStart,
              metrics.trackSize,
              metrics.thumbSize,
              metrics.maxScroll,
            );
            orientation === "horizontal"
              ? track.current.scrollTo(newScroll, 0)
              : track.current.scrollTo(0, newScroll);
            updateVisibleThumb();
          };

          const handleMouseUp = () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
            dragCleanupRef.current = null;
            setIsDragging(false);
          };

          document.addEventListener("mousemove", handleMouseMove);
          document.addEventListener("mouseup", handleMouseUp);
          dragCleanupRef.current = handleMouseUp;
        }}
        onScroll={(e) => {
          scroll(
            orientation === "vertical"
              ? e.currentTarget.scrollTop
              : e.currentTarget.scrollLeft,
          );
          updateVisibleThumb();
        }}
      >
        <div>
          <div
            className={styles.thumb}
            style={
              orientation === "horizontal"
                ? { width: scrollSize }
                : { height: scrollSize }
            }
          />
        </div>
      </div>
      <div ref={thumbRef} className={styles.visibleThumb} />
    </div>
  );
}

export { ScrollBar };
